import React from 'react';
import Icon from '../../../components/AppIcon';

const TestimonySection = () => {
  const testimonyChallenges = [
    {
      challenge: "Methodology Attacks",
      description: "Opposing counsel questioning valuation approaches, assumptions, and calculation methods",
      preparation: [
        "Comprehensive methodology documentation",
        "Alternative approach consideration and rejection rationale",
        "Sensitivity analysis for key assumptions",
        "Peer review and quality control processes"
      ],
      icon: "Target"
    },
    {
      challenge: "Qualification Challenges",
      description: "Attempts to disqualify expert based on experience, credentials, or potential conflicts",
      preparation: [
        "Detailed CV with relevant experience emphasis",
        "Professional certification maintenance records",
        "Conflict checking documentation",
        "Similar case experience portfolio"
      ],
      icon: "Award"
    },
    {
      challenge: "Data Reliability Questions",
      description: "Scrutiny of data sources, adjustments, and normalization procedures used in analysis",
      preparation: [
        "Data source verification and documentation",
        "Adjustment rationale with supporting evidence",
        "Alternative data source consideration",
        "Industry standard benchmarking"
      ],
      icon: "Database"
    },
    {
      challenge: "Opinion Consistency",
      description: "Cross-examination on consistency with prior testimony or published positions",
      preparation: [
        "Prior testimony review and reconciliation",
        "Published work consistency verification",
        "Position evolution documentation",
        "Fact-specific distinction preparation"
      ],
      icon: "Scale"
    }
  ];

  const courtSystemFamiliarity = [
    {
      jurisdiction: "US State Courts",
      experience: "25+ cases",
      specialties: ["Commercial disputes", "Partnership dissolutions", "IP damages"],
      keyInsights: "Emphasis on written expert reports with detailed methodology explanations"
    },
    {
      jurisdiction: "Dubai International Financial Centre",
      experience: "15+ cases",
      specialties: ["Cross-border disputes", "Financial services", "Investment disputes"],
      keyInsights: "International arbitration experience with complex financial instruments"
    },
    {
      jurisdiction: "Bahrain Commercial Courts",
      experience: "12+ cases",
      specialties: ["Banking disputes", "Real estate valuations", "Family business conflicts"],
      keyInsights: "Strong preference for local market data and regional comparables"
    },
    {
      jurisdiction: "Qatar Civil & Commercial Courts",
      experience: "10+ cases",
      specialties: ["Construction disputes", "Joint venture valuations", "Regulatory matters"],
      keyInsights: "Detailed documentation requirements with Arabic translation needs"
    }
  ];

  const communicationSkills = [
    {
      skill: "Complex Concept Simplification",
      description: "Ability to explain technical valuation concepts in accessible language for judges and juries",
      examples: ["DCF methodology using simple analogies", "Risk premium explanations with real-world examples"]
    },
    {
      skill: "Visual Communication",
      description: "Effective use of charts, graphs, and demonstrative aids to support testimony",
      examples: ["Interactive valuation models", "Timeline graphics for damages periods"]
    },
    {
      skill: "Composure Under Pressure",
      description: "Maintaining professionalism and clarity during aggressive cross-examination",
      examples: ["Calm responses to hostile questioning", "Consistent message delivery under stress"]
    },
    {
      skill: "Cultural Sensitivity",
      description: "Understanding of regional business practices and cultural context in US & GCC markets",
      examples: ["Family business dynamics", "IFRS Financial Reporting", "Local market customs"]
    }
  ];

  return (

    <>
    {/* <section className="py-20 lg:py-28 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center bg-purple-100 text-purple-700 rounded-full px-4 py-2 text-sm font-medium mb-6">
            <Icon name="MessageSquare" size={16} className="mr-2" />
            Courtroom Excellence
          </div>
          
          <h2 className="text-3xl lg:text-4xl font-bold text-primary mb-6">
            Defending Opinions Under Cross-Examination
          </h2>
          
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Our extensive courtroom experience and systematic preparation ensure credible, defensible testimony that withstands rigorous cross-examination.
          </p>
        </div>

        <div className="mb-20">
          <h3 className="text-2xl font-bold text-primary text-center mb-12">
            Common Cross-Examination Challenges & Our Preparation
          </h3>
          
          <div className="grid lg:grid-cols-2 gap-8">
            {testimonyChallenges?.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-sm border border-border hover:shadow-md transition-shadow duration-300">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Icon name={item?.icon} size={24} className="text-purple-600" />
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-primary mb-2">
                      {item?.challenge}
                    </h4>
                    <p className="text-text-secondary">
                      {item?.description}
                    </p>
                  </div>
                </div>

                <div>
                  <h5 className="text-sm font-semibold text-primary mb-4 uppercase tracking-wide">
                    Our Preparation Strategy
                  </h5>
                  <div className="space-y-3">
                    {item?.preparation?.map((prep, idx) => (
                      <div key={idx} className="flex items-start space-x-3">
                        <Icon name="CheckCircle" size={16} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-text-secondary">{prep}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-20">
          <h3 className="text-2xl font-bold text-primary text-center mb-12">
          US & GCC Court System Expertise
          </h3>
          
          <div className="grid md:grid-cols-2 gap-8">
            {courtSystemFamiliarity?.map((court, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-sm border border-border">
                <div className="flex items-center justify-between mb-6">
                  <h4 className="text-lg font-semibold text-primary">
                    {court?.jurisdiction}
                  </h4>
                  <span className="bg-sky-100 text-sky-700 px-3 py-1 rounded-full text-sm font-medium">
                    {court?.experience}
                  </span>
                </div>

                <div className="space-y-4">
                  <div>
                    <h5 className="text-sm font-semibold text-primary mb-2">Specialties</h5>
                    <div className="flex flex-wrap gap-2">
                      {court?.specialties?.map((specialty, idx) => (
                        <span key={idx} className="bg-slate-100 text-slate-700 px-3 py-1 rounded-full text-xs">
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="bg-sky-50 border-l-4 border-sky-400 p-4 rounded-r-lg">
                    <h5 className="text-sm font-semibold text-sky-800 mb-1">Key Insight</h5>
                    <p className="text-sm text-sky-700">{court?.keyInsights}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-sm border border-border">
          <div className="text-center mb-12">
            <h3 className="text-2xl lg:text-3xl font-bold text-primary mb-4">
              Effective Courtroom Communication
            </h3>
            <p className="text-text-secondary max-w-2xl mx-auto">
              Our testimony effectiveness stems from clear communication, cultural awareness, and professional composure under pressure.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {communicationSkills?.map((skill, index) => (
              <div key={index} className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                    <span className="text-purple-600 font-bold text-sm">{index + 1}</span>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-semibold text-primary mb-2">
                      {skill?.skill}
                    </h4>
                    <p className="text-text-secondary mb-4 leading-relaxed">
                      {skill?.description}
                    </p>
                    <div className="space-y-2">
                      {skill?.examples?.map((example, idx) => (
                        <div key={idx} className="flex items-center space-x-2">
                          <Icon name="ArrowRight" size={14} className="text-purple-500" />
                          <span className="text-sm text-text-secondary">{example}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 pt-8 border-t border-border">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-3xl font-bold text-purple-600 mb-2">50+</div>
                <div className="text-sm text-text-secondary">Court Testimonies</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-purple-600 mb-2">100+</div>
                <div className="text-sm text-text-secondary">Deposition Hours</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-purple-600 mb-2">95%</div>
                <div className="text-sm text-text-secondary">Opinion Acceptance</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-purple-600 mb-2">15+</div>
                <div className="text-sm text-text-secondary">Jurisdictions</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section> */}
    </>
  );
};

export default TestimonySection;